/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { EnumExtensions } from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';

import Button from '../button/Button';
import { Popover } from '../popover';

/**
 * Resources for the price editor mode selection.
 */
export interface IPriceEditorModeSelectionResources {
    specifyAmountButtonText: string;
    payFullAmountButtonText: string;
}

/**
 * Props for the price editor mode selection.
 */
export interface IPriceEditorModeSelectionProps {
    className: string;

    resources: IPriceEditorModeSelectionResources;

    currentState: PriceEditorModeSelectionState;
    onStateChanged(state: PriceEditorModeSelectionState): void;
}

/**
 * Defines editor modes for the amount.
 */
export enum PriceEditorModeSelectionState {
    full = 0,
    partial = 1
}

/**
 * Retrieves resource value based by the given mode state.
 * @param modeState - Given state.
 * @param resources - All resources.
 * @returns Resource value for the given state.
 */
const getPriceEditorModeSelectionStateResources = (modeState: PriceEditorModeSelectionState, resources: IPriceEditorModeSelectionResources): string => {
    switch (modeState) {
        case PriceEditorModeSelectionState.full:
            return resources.payFullAmountButtonText;
        case PriceEditorModeSelectionState.partial:
            return resources.specifyAmountButtonText;
        default:
            throw new Error('Failed to retrieve resource description for unknown price editor selection state.');
    }
};

/**
 * On click functionality.
 * @param handleStateChange - Handle change state function.
 * @param modeState - New state.
 * @returns Event handler.
 */
const onClickHandler = (handleStateChange: (modeState: PriceEditorModeSelectionState) => void, modeState: PriceEditorModeSelectionState) => () => {
    handleStateChange(modeState);
};

/**
 * Renders a button that allows to update the mode to the given state.
 * @param modeState - The state to render.
 * @param resources - Resource values.
 * @param className - Element class name.
 * @param handleStateChange - Event handler to update the state.
 * @returns React node.
 */
const renderModeStateItem = (
    modeState: PriceEditorModeSelectionState,
    resources: IPriceEditorModeSelectionResources,
    className: string,
    handleStateChange: (modeState: PriceEditorModeSelectionState) => void): React.ReactNode => {

    const stateStringRepresentation = PriceEditorModeSelectionState[modeState];

    return (
        <button className={`${className}__item__${stateStringRepresentation}`} onClick={onClickHandler(handleStateChange, modeState)}>
            {getPriceEditorModeSelectionStateResources(modeState, resources)}
        </button>
    );
};

/**
 * On Toggle Popover functionality.
 * @param setPopoverState -Set popover state function.
 * @param isPopoverState -Boolean value.
 * @returns Set toggle popover state functionality.
 */
const togglePopoverHandler = (setPopoverState: React.Dispatch<React.SetStateAction<boolean>>, isPopoverState: boolean) => () => {
    setPopoverState(!isPopoverState);
};

/**
 * Price editor component which handles selection modes (for instance, full or partial amount).
 * @param props - Configuration of the component.
 * @returns React element.
 */
export const PriceEditorModeSelectionComponent: React.FC<IPriceEditorModeSelectionProps> = (props: IPriceEditorModeSelectionProps) => {
    const popoverReference = React.createRef<HTMLButtonElement>();
    const [isPopoverShown, setIsPopoverShown] = React.useState(false);

    const [modeState, setModeState] = React.useState(props.currentState);

    /**
     * Updates component state when a selection is changed.
     * @param clickedState - New mode state.
     */
    const onChangeMode = (clickedState: PriceEditorModeSelectionState) => {
        setModeState(clickedState);
        setIsPopoverShown(false);
        props.onStateChanged(clickedState);
    };

    return (
        <div className={props.className}>
            <Button
                className={`${props.className}__expand-modes-button`}
                innerRef={popoverReference}
                aria-expanded={isPopoverShown}
                aria-describedby={props.className}
                onClick={togglePopoverHandler(setIsPopoverShown, isPopoverShown)}
            >
                {getPriceEditorModeSelectionStateResources(modeState, props.resources)}
            </Button>
            <Popover
                id={props.className}
                placement='bottom-end'
                isOpen={isPopoverShown}
                target={popoverReference}
                toggle={togglePopoverHandler(setIsPopoverShown, isPopoverShown)}
            >
                {
                    EnumExtensions.getEnumValues<PriceEditorModeSelectionState>(PriceEditorModeSelectionState).map(state => {
                        return renderModeStateItem(state, props.resources, props.className, onChangeMode);
                    })
                }
            </Popover>
        </div>
    );
};
